import React, { useState, useEffect } from 'react';
import { X, Calendar, Search, Clock } from 'lucide-react';
import api from '../../services/api';

const INITIAL_FORM = {
  area: '',
  morador_id: '',
  data: '',
  hora_inicio: '',
  hora_fim: '',
  status: 'Reservado'
};

const ReservaModal = ({ isOpen, onClose, moradores, refreshData, reservasExistentes, espacos, dataInicial = '' }) => {

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [searchTerm, setSearchTerm] = useState("");
  const [showResults, setShowResults] = useState(false);

  // Reinicia o formulário ao abrir/fechar
  useEffect(() => {
    if (isOpen) {
      setFormData({ ...INITIAL_FORM, data: dataInicial });
      setSearchTerm("");
    }
  }, [isOpen, dataInicial]);

  useEffect(() => {
    if (formData.area && !espacos.some((espaco) => espaco.nome === formData.area)) {
      setFormData((current) => ({ ...current, area: '', hora_inicio: '', hora_fim: '' }));
    }
  }, [espacos, formData.area]);

  // Lista de horários cheios das 08h às 22h
  const horariosDisponiveis = Array.from({ length: 15 }, (_, i) => `${String(i + 8).padStart(2, '0')}:00`);

  // Filtro de moradores para a busca
  const moradoresFiltrados = moradores.filter(m =>
    String(m.nome ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(m.unidade ?? '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectMorador = (morador) => {
    setFormData({ ...formData, morador_id: morador.id });
    setSearchTerm(`${morador.nome} - Unid: ${morador.unidade}`);
    setShowResults(false);
  };

  const handleCliqueHora = (hora) => {
    // Lógica para selecionar início e depois fim
    if (!formData.hora_inicio || (formData.hora_inicio && formData.hora_fim)) {
      setFormData({ ...formData, hora_inicio: hora, hora_fim: '' });
    } else {
      if (hora > formData.hora_inicio) {
        setFormData({ ...formData, hora_fim: hora });
      } else {
        setFormData({ ...formData, hora_inicio: hora, hora_fim: '' });
      }
    }
  };

  const handleOpenDatePicker = (event) => {
    if (typeof event.currentTarget.showPicker === 'function') {
      try {
        event.currentTarget.showPicker();
      } catch (error) {
        // O navegador pode bloquear showPicker fora de uma ação direta do usuário.
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.morador_id || !formData.hora_fim) {
        return alert("Preencha todos os campos, incluindo o horário de fim!");
    }
    
    try {
      await api.post('/agenda/', formData);
      alert("Reserva confirmada!");
      refreshData();
      onClose();
    } catch (error) {
      alert("Erro ao gravar reserva.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-slate-50/50">
          <h2 className="text-xl font-bold text-slate-800">Nova Reserva</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          
          {/* Seleção de Área */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Área Comum</label>
            <select 
              required
              className="w-full border border-gray-200 rounded-xl p-3 bg-gray-50 outline-none focus:ring-2 focus:ring-slate-800 transition-all text-slate-700 font-medium"
              value={formData.area}
              onChange={(e) => setFormData({...formData, area: e.target.value})}
            >
              <option value="">Selecione o espaço...</option>
              {espacos.map((espaco) => (
                <option key={espaco.id} value={espaco.nome}>{espaco.nome}</option>
              ))}
            </select>
          </div>

          {/* Busca de Morador */}
          <div className="relative">
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Morador Responsável</label>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input 
                type="text"
                placeholder="Pesquisar morador..."
                className="w-full border border-gray-200 rounded-xl p-3 pl-10 bg-gray-50 outline-none focus:ring-2 focus:ring-slate-800 transition-all"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setShowResults(true); }}
                onFocus={() => setShowResults(true)}
              />
            </div>
            {showResults && searchTerm.length > 0 && (
              <div className="absolute w-full mt-1 bg-white border border-gray-100 rounded-xl shadow-xl z-20 max-h-40 overflow-y-auto">
                {moradoresFiltrados.map(m => (
                  <div key={m.id} onClick={() => handleSelectMorador(m)} className="p-3 hover:bg-slate-50 cursor-pointer flex justify-between items-center border-b last:border-0">
                    <span className="text-sm font-semibold text-slate-700">{m.nome}</span>
                    <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-1 rounded font-bold">Unid: {m.unidade}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Data */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Data da Reserva</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input 
                type="date"
                required
                className="w-full cursor-pointer border border-gray-200 rounded-xl p-3 pl-10 bg-gray-50 outline-none focus:ring-2 focus:ring-slate-800"
                value={formData.data}
                onClick={handleOpenDatePicker}
                onFocus={handleOpenDatePicker}
                onChange={(e) => setFormData({...formData, data: e.target.value})}
              />
            </div>
          </div>

          {/* Grade de Horários (Estilo Escuro) */}
          {formData.area && formData.data && (
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-3">Grade de Horários</label>
              <div className="grid grid-cols-4 gap-2">
                {horariosDisponiveis.map((hora) => {
                  const reservasDia = reservasExistentes.filter(r => r.area === formData.area && r.data === formData.data && r.status !== "Cancelado");
                  const estaReservado = reservasDia.some(r => hora >= r.hora_inicio && hora < r.hora_fim);
                  
                  const isSelected = hora === formData.hora_inicio || hora === formData.hora_fim;
                  const isInRange = hora > formData.hora_inicio && hora < formData.hora_fim;

                  return (
                    <button
                      key={hora}
                      type="button"
                      disabled={estaReservado}
                      onClick={() => handleCliqueHora(hora)}
                      className={`p-2 text-xs font-mono rounded-lg border transition-all ${
                        estaReservado 
                          ? 'bg-slate-900 text-slate-600 border-slate-800 opacity-40 cursor-not-allowed' 
                          : isSelected || isInRange
                            ? 'bg-slate-800 text-white border-slate-800 ring-2 ring-teal-300/80 scale-[1.02] z-10'
                            : 'bg-white text-slate-600 border-gray-200 hover:border-slate-400'
                      }`}
                    >
                      {hora}
                    </button>
                  );
                })}
              </div>
              {formData.hora_inicio && (
                <div className="mt-3 flex items-center gap-2 justify-center p-2 bg-slate-100 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-700">
                  <Clock size={14} />
                  Intervalo: {formData.hora_inicio} {formData.hora_fim ? `às ${formData.hora_fim}` : '...'}
                </div>
              )}
            </div>
          )}

          {/* Botão de Ação */}
          <button 
            type="submit" 
            disabled={!formData.hora_fim}
            className={`w-full py-4 rounded-xl font-bold transition-all shadow-lg mt-4 active:scale-95 ${
              !formData.hora_fim 
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
              : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            Confirmar Reserva
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReservaModal;
